import React from "react";

const linkStyle: React.CSSProperties = {
  color: "#2A3D4A",
  textDecoration: "underline",
};

const PrivacyPolicy: React.FC = () => {
  return (
    <div
      style={{
        padding: "clamp(32px, 5vw, 48px) clamp(24px, 5vw, 40px)",
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      {/* Privacy Policy */}
      <section style={{ marginBottom: "56px" }}>
        <p
          style={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "#2A3D4A",
            margin: "0 0 16px",
          }}
        >
          Last Updated: September 6, 2026
        </p>
        <h2
          style={{
            fontSize: "clamp(1.75rem, 4vw, 2.375rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            color: "#2A3D4A",
            margin: "0 0 32px",
          }}
        >
          Privacy Policy
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
          <PolicySection title="Introduction">
            <p>
              Refine ("we," "our," or "us") is a habit-tracking mobile app for
              iOS. This Privacy Policy explains what information the App uses,
              how that information is handled, and the choices you have.
            </p>
            <p style={{ marginTop: "8px" }}>
              By using Refine, you agree to this Privacy Policy. If you do not
              agree, please do not use the App.
            </p>
          </PolicySection>

          <PolicySection title="Who We Are">
            <p>Refine is an independent app by Sky Stanoyevitch.</p>
            <p style={{ marginTop: "8px" }}>
              Contact:{" "}
              <a href="mailto:skystanoyevitch@proton.me" style={linkStyle}>
                skystanoyevitch@proton.me
              </a>
            </p>
            <p style={{ marginTop: "8px" }}>
              Website privacy page:{" "}
              <a
                href="https://www.skystanoyevitch.com/strong-habit/privacy"
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.skystanoyevitch.com/strong-habit/privacy
              </a>
            </p>
          </PolicySection>

          <PolicySection title="Summary">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                Your habits, streaks, completions, notes, and most settings stay
                on your device by default.
              </li>
              <li>
                We do not run a Refine account system and we do not host your
                habit journal on our own servers.
              </li>
              <li>
                Premium AI features may send limited habit context to Google
                Gemini through Firebase AI Logic so the App can generate
                personalized feedback.
              </li>
              <li>
                We use Firebase Analytics, Firebase Remote Config, Firebase App
                Check, RevenueCat, and Apple for subscriptions, reliability, and
                product improvement.
              </li>
              <li>
                You can export or delete your local data from the App, or remove
                it by uninstalling.
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="Information Stored on Your Device">
            <p style={{ marginBottom: "12px" }}>
              Refine stores the following on your device (and, where applicable,
              in the App Group used by widgets):
            </p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                Habit names, descriptions, schedules, colors, icons, and related
                settings
              </li>
              <li>
                Completion history, streaks, challenge progress, and archive
                state
              </li>
              <li>
                Profile display name and optional profile photo you choose
              </li>
              <li>Reminder and notification preferences</li>
              <li>Saved Discover bookmarks and saved Refine notes</li>
              <li>
                Weekly, monthly, and yearly review drafts you keep or discard
              </li>
              <li>Backup files you create in the App</li>
              <li>
                Subscription and entitlement flags needed to unlock Premium
                features
              </li>
              <li>App preferences such as theme and onboarding state</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              This core journal data is not uploaded to a Refine-owned cloud
              account. Manual and automatic backups you create stay under your
              control on device (and wherever you choose to share or save those
              files).
            </p>
          </PolicySection>

          <PolicySection title="Information Processed Through Third-Party Services">
            <h4 style={subheadingStyle}>Apple</h4>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                App Store purchases, restores, and subscription billing are
                handled by Apple.
              </li>
              <li>
                Local notifications are scheduled on your device through Apple
                frameworks.
              </li>
              <li>
                If you use on-device Apple Intelligence features where available,
                processing follows Apple's privacy terms for that capability.
              </li>
              <li>We do not receive your full payment card details.</li>
            </ul>

            <h4 style={{ ...subheadingStyle, marginTop: "20px" }}>
              RevenueCat
            </h4>
            <p>
              We use RevenueCat to manage subscription status and entitlements
              (including promo or lifetime access where applicable). RevenueCat
              may process device identifiers, purchase tokens, and subscription
              status.
            </p>
            <p style={{ marginTop: "8px" }}>
              RevenueCat Privacy Policy:{" "}
              <a
                href="https://www.revenuecat.com/privacy"
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.revenuecat.com/privacy
              </a>
            </p>

            <h4 style={{ ...subheadingStyle, marginTop: "20px" }}>
              Firebase (Google)
            </h4>
            <p style={{ marginBottom: "12px" }}>
              Refine uses Google Firebase services, including:
            </p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                <strong style={{ fontWeight: 600 }}>Firebase Analytics:</strong>{" "}
                anonymous or pseudonymous usage events (for example screen
                views, feature use, and purchase funnels) to understand product
                health
              </li>
              <li>
                <strong style={{ fontWeight: 600 }}>
                  Firebase Remote Config:
                </strong>{" "}
                feature flags and AI model settings so we can enable, disable,
                or tune features safely
              </li>
              <li>
                <strong style={{ fontWeight: 600 }}>Firebase App Check:</strong>{" "}
                device integrity checks that help protect AI and backend
                endpoints from abuse
              </li>
              <li>
                <strong style={{ fontWeight: 600 }}>
                  Cloud Firestore (limited):
                </strong>{" "}
                promo-code validation and redemption tracking when you redeem a
                code
              </li>
              <li>
                <strong style={{ fontWeight: 600 }}>
                  Firebase AI Logic / Google Gemini:
                </strong>{" "}
                when a Premium AI feature runs, habit context needed for that
                request (such as habit name, description, recent consistency,
                streak or challenge context, and short answers you provide) may
                be sent to Google to generate a response
              </li>
            </ul>
            <p style={{ marginTop: "8px" }}>
              Google Privacy Policy:{" "}
              <a
                href="https://policies.google.com/privacy"
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                https://policies.google.com/privacy
              </a>
            </p>
          </PolicySection>

          <PolicySection title="Widgets">
            <p>
              If you add Refine widgets to your Home Screen, habit summary data
              is shared with the widget extension through an App Group on your
              device so the widget can render. Widget unlock status follows your
              Premium entitlement.
            </p>
          </PolicySection>

          <PolicySection title="AI Features and Habit Context">
            <p>
              Premium AI features may include personalized Refine notes, recover
              guidance, weekly review advice, Discover blurbs, and related habit
              insights.
            </p>
            <p style={{ marginTop: "12px", marginBottom: "12px" }}>
              When those features run:
            </p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                The App builds a short, purpose-built prompt from your habit
                data and any answers you enter
              </li>
              <li>
                That prompt may be processed by Google Gemini via Firebase AI
                Logic, and in some cases by on-device Apple models
              </li>
              <li>
                Generated text is returned to the App so you can read, save,
                archive, or delete it
              </li>
              <li>
                Saved Refine notes are stored locally and shown on the matching
                habit detail screen
              </li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              AI processing is gated behind Premium entitlement and remote
              feature controls. If AI is unavailable, the App may show a local
              fallback message instead of calling a model.
            </p>
            <p style={{ marginTop: "12px" }}>
              We ask you not to enter sensitive personal data (health diagnoses,
              financial account numbers, government IDs, or anyone else's
              private information) into AI prompts or habit fields.
            </p>
          </PolicySection>

          <PolicySection title="How We Use Information">
            <p style={{ marginBottom: "12px" }}>
              We use the information described above to:
            </p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                Run habit tracking, reminders, challenges, Discover, reviews,
                and widgets
              </li>
              <li>
                Calculate streaks, consistency, and related stats on device
              </li>
              <li>Deliver Premium AI feedback when you request it</li>
              <li>
                Manage subscriptions, restores, trials, and promo redemptions
              </li>
              <li>
                Measure product usage and reliability through analytics
              </li>
              <li>Protect AI and related endpoints with App Check</li>
              <li>Improve Refine over time</li>
            </ul>
          </PolicySection>

          <PolicySection title="What We Do Not Do">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>We do not sell your personal information</li>
              <li>
                We do not create a public social profile from your habits
              </li>
              <li>
                We do not require an email/password Refine account to track
                habits
              </li>
              <li>
                We do not use your habit journal to train our own public
                marketing models outside the AI providers named here
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="Notifications">
            <p style={{ marginBottom: "12px" }}>If you enable reminders:</p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>Schedules and copy live on your device</li>
              <li>Notifications are delivered locally by the system</li>
              <li>
                We do not read the content of your local notifications from a
                Refine server
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="Data Retention">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                Local habit data remains until you delete habits, clear App
                data, or uninstall
              </li>
              <li>
                Saved Refine notes remain until you archive or delete them in
                the habit detail screen
              </li>
              <li>
                Backups remain until you delete them in Data Management or
                remove them from device storage
              </li>
              <li>
                Analytics and subscription records held by Apple, RevenueCat,
                Firebase, or Google are retained according to those providers'
                policies
              </li>
              <li>
                Promo redemption records may be retained to prevent abuse and
                honor lifetime access
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="Your Choices and Controls">
            <p style={{ marginBottom: "12px" }}>You can:</p>
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>Create, edit, archive, or delete habits in the App</li>
              <li>Export a data backup from Data Management</li>
              <li>Turn reminders on or off</li>
              <li>
                Save, archive, or delete Refine notes on each habit's detail
                screen
              </li>
              <li>Cancel a subscription in Apple subscription settings</li>
              <li>
                Limit analytics through iOS privacy settings where available
              </li>
              <li>
                Uninstall the App to remove on-device Refine data (this does not
                automatically cancel an App Store subscription)
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="Children's Privacy">
            <p>
              Refine is not directed to children under 13. We do not knowingly
              collect personal information from children under 13. If you
              believe a child has provided personal information through the App,
              contact us and we will take appropriate steps.
            </p>
          </PolicySection>

          <PolicySection title="International Processing">
            <p>
              Third-party providers may process data in the United States or
              other countries. When you use Premium AI, analytics, Remote
              Config, App Check, promo validation, or subscriptions, your
              information may be processed outside your home country under those
              providers' safeguards and policies.
            </p>
          </PolicySection>

          <PolicySection title="Security">
            <p>
              We use industry-standard platform storage, App Transport
              protections, App Check, and provider security controls. No method
              of electronic storage or transmission is perfectly secure. Protect
              your device with a passcode, Face ID, or Touch ID.
            </p>
          </PolicySection>

          <PolicySection title="Changes to This Policy">
            <p>
              We may update this Privacy Policy. When we do, we will revise the
              "Last Updated" date and post the updated policy at{" "}
              <a
                href="https://www.skystanoyevitch.com/strong-habit/privacy"
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.skystanoyevitch.com/strong-habit/privacy
              </a>
              . Continued use of the App after an update means you accept the
              revised policy.
            </p>
          </PolicySection>

          <PolicySection title="Contact">
            <p>Questions about privacy or this policy:</p>
            <p style={{ marginTop: "8px" }}>
              Email:{" "}
              <a href="mailto:skystanoyevitch@proton.me" style={linkStyle}>
                skystanoyevitch@proton.me
              </a>
            </p>
            <p style={{ marginTop: "8px" }}>
              Web:{" "}
              <a
                href="https://www.skystanoyevitch.com/strong-habit/privacy"
                style={linkStyle}
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.skystanoyevitch.com/strong-habit/privacy
              </a>
            </p>
            <p style={{ marginTop: "12px" }}>
              This policy was last updated on September 6, 2026.
            </p>
          </PolicySection>
        </div>
      </section>

      {/* Divider */}
      <div
        style={{
          height: "1px",
          background: "rgba(42,61,74,0.2)",
          marginBottom: "56px",
        }}
      />

      {/* Terms of Service */}
      <section>
        <p
          style={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase" as const,
            color: "#2A3D4A",
            margin: "0 0 16px",
          }}
        >
          Last updated June 27, 2025
        </p>
        <h2
          style={{
            fontSize: "clamp(1.75rem, 4vw, 2.375rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            color: "#2A3D4A",
            margin: "0 0 32px",
          }}
        >
          Terms of Service
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
          <PolicySection title="1. Acceptance of Terms">
            <p>
              By downloading, installing, or using Refine, you agree to be
              bound by these Terms of Service. If you do not agree, do not use
              the App.
            </p>
          </PolicySection>

          <PolicySection title="2. Description of Service">
            <p>Refine is a habit tracking application that allows you to:</p>
            <ul
              style={{ paddingLeft: "20px", lineHeight: 2, marginTop: "8px" }}
            >
              <li>Create and track daily, weekly, or monthly habits</li>
              <li>Monitor streaks and progress</li>
              <li>View statistics on habit completion</li>
              <li>Set reminders for habit completion</li>
              <li>Use Streak Protection to preserve momentum</li>
            </ul>
          </PolicySection>

          <PolicySection title="3. User Responsibilities">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>Use the App for lawful purposes only</li>
              <li>
                Take responsibility for all activity under your device
              </li>
              <li>Maintain the security of your device and data</li>
            </ul>
          </PolicySection>

          <PolicySection title="4. Data & Privacy">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>All user data is stored locally on your device</li>
              <li>
                We do not collect, store, or transmit personal information
              </li>
              <li>See the Privacy Policy above for full details</li>
            </ul>
          </PolicySection>

          <PolicySection title="5. Intellectual Property">
            <p>
              The App and all content, features, and functionality are owned by
              Refine and protected by copyright and applicable intellectual
              property law. You may not modify, reproduce, or distribute the
              App without express written permission.
            </p>
          </PolicySection>

          <PolicySection title="6. Limitation of Liability">
            <ul style={{ paddingLeft: "20px", lineHeight: 2, margin: 0 }}>
              <li>
                The App is provided "as is" without warranties of any kind
              </li>
              <li>
                We are not liable for any damages arising from use of or
                inability to use the App
              </li>
              <li>
                We are not responsible for any failure to achieve personal
                goals or habit formation
              </li>
            </ul>
          </PolicySection>

          <PolicySection title="7. Changes to Terms">
            <p>
              We may modify these Terms at any time. Continued use of the App
              following changes constitutes acceptance of the modified Terms.
            </p>
          </PolicySection>

          <PolicySection title="8. Contact">
            <p>Questions about these Terms?</p>
            <p
              style={{ marginTop: "8px", color: "#2A3D4A", fontWeight: 500 }}
            >
              skystanoyevitch@proton.me
            </p>
          </PolicySection>
        </div>
      </section>
    </div>
  );
};

interface PolicySectionProps {
  title: string;
  children: React.ReactNode;
}

const subheadingStyle: React.CSSProperties = {
  fontFamily: "'Outfit', sans-serif",
  fontSize: "15px",
  fontWeight: 600,
  color: "#2A3D4A",
  margin: "0 0 10px",
  letterSpacing: "-0.01em",
};

const PolicySection: React.FC<PolicySectionProps> = ({ title, children }) => (
  <div
    style={{
      borderLeft: "1.5px solid #2A3D4A",
      paddingLeft: "20px",
    }}
  >
    <h3
      style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: "17px",
        fontWeight: 700,
        color: "#2A3D4A",
        margin: "0 0 10px",
        letterSpacing: "-0.01em",
      }}
    >
      {title}
    </h3>
    <div
      style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: "15px",
        fontWeight: 300,
        color: "#2A3D4A",
        lineHeight: 1.65,
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </div>
  </div>
);

export default PrivacyPolicy;
